import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  message, 
  Popconfirm,
  Spin,
  Space
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const StarPrice = ({ user }) => {
  const { apiClient } = useAuth();
  const [starLevels, setStarLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  // 获取星级配置数据
  const fetchStarLevels = async () => {
    if (!apiClient) {
      console.warn('API客户端未初始化');
      return;
    }
    
    setLoading(true);
    try {
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'STAR_LEVEL',
        colName: 'STAR_LEVEL',
      });
      
      if (resp.data && resp.data.code === 200) {
        let rawData = resp.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        
        // 构建表格数据
        const levels = (rawData || []).map((item) => {
          console.log('原始数据项:', item); // 调试信息
          return {
            configId: item.configId,
            enumCode: item.enumCode || item.enumName,
            enumName: item.enumName, // 保存原始的enumName字段
            enumLabel: item.enumName,
            chp: item.remark ? JSON.parse(item.remark)?.chp || 0 : 0,
            showSort: item.showSort !== undefined && item.showSort !== null ? item.showSort : 0, // 更严格的判断
            remark: item.remark || '',
            state: item.state || 1,
            parentEnumCode: item.parentEnumCode || 0
          }
        }).sort((a, b) => (a.showSort || 0) - (b.showSort || 0)); // 按showSort排序
        
        console.log('处理后的数据:', levels); // 调试信息
        
        setStarLevels(levels);
      } else {
        message.error('获取星级配置失败: ' + (resp.data?.msg || '未知错误'));
      }
    } catch (error) {
      console.error('获取星级配置异常:', error);
      message.error('获取星级配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存星级配置
  const saveStarLevel = async (values) => {
    if (!apiClient) {
      message.error('系统初始化中，请稍后重试');
      return;
    }
    
    console.log('保存的表单值:', values); // 调试信息
    console.log('编辑记录:', editingRecord); // 调试信息
    
    // 确保showSort值存在
    if (values.showSort === undefined || values.showSort === null) {
      console.warn('showSort值缺失，使用默认值0');
      values.showSort = 0;
    }
    
    try {
      // 构造remark字段
      const remark = JSON.stringify({ chp: values.chp });
      
      const payload = {
        tableName: 'STAR_LEVEL',
        colName: 'STAR_LEVEL',
        enumName: values.enumName,
        enumCode: values.enumName, // 星级值与星级名称保持一致
        remark: remark,
        showSort: values.showSort, // 现在确保有值
        state: 1,
        parentEnumCode: 0,
        configId: editingRecord?.configId || null
      };
      
      console.log('发送的payload:', payload); // 调试信息
      
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      
      if (resp.data && resp.data.code === 200) {
        message.success(editingRecord ? '修改成功' : '新增成功');
        setModalVisible(false);
        form.resetFields();
        setEditingRecord(null);
        
        // 清除缓存
        localStorage.removeItem('STAR_LEVEL');
        console.log('已清除STAR_LEVEL缓存');
        
        // 重新获取数据
        fetchStarLevels();
      } else {
        message.error(resp.data?.msg || (editingRecord ? '修改失败' : '新增失败'));
      }
    } catch (error) {
      console.error('保存星级配置异常:', error);
      message.error(editingRecord ? '修改失败' : '新增失败');
    }
  };

  // 删除星级配置
  const deleteStarLevel = async (record) => {
    if (!apiClient) {
      message.error('系统初始化中，请稍后重试');
      return;
    }
    
    try {
      const resp = await apiClient.delete(`/portal-server-prod/sys/enumconfig/v1/delete/${record.configId}`);
      
      if (resp.data && resp.data.code === 200) {
        message.success('删除成功');
        
        // 清除缓存
        localStorage.removeItem('STAR_LEVEL');
        console.log('已清除STAR_LEVEL缓存');
        
        // 重新获取数据
        fetchStarLevels();
      } else {
        message.error(resp.data?.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除星级配置异常:', error);
      message.error('删除失败');
    }
  };

  // 上移星级配置
  const moveUpStarLevel = async (record) => {
    if (!apiClient) {
      message.error('系统初始化中，请稍后重试');
      return;
    }
    
    try {
      // 获取当前排序位置
      const currentIndex = starLevels.findIndex(item => item.configId === record.configId);
      if (currentIndex <= 0) {
        message.warning('已经是第一个，无法上移');
        return;
      }
      
      // 获取上一个记录
      const prevRecord = starLevels[currentIndex - 1];
      
      // 交换showSort值
      const currentShowSort = record.showSort || 0;
      const prevShowSort = prevRecord.showSort || 0;
      
      // 更新当前记录
      const currentPayload = {
        tableName: 'STAR_LEVEL',
        colName: 'STAR_LEVEL',
        enumName: record.enumName,
        enumCode: record.enumCode || record.enumName,
        remark: JSON.stringify({ chp: record.chp }),
        showSort: prevShowSort,
        state: record.state || 1,
        parentEnumCode: record.parentEnumCode || 0,
        configId: record.configId
      };
      
      // 更新上一个记录
      const prevPayload = {
        tableName: 'STAR_LEVEL',
        colName: 'STAR_LEVEL',
        enumName: prevRecord.enumName,
        enumCode: prevRecord.enumCode || prevRecord.enumName,
        remark: JSON.stringify({ chp: prevRecord.chp }),
        showSort: currentShowSort,
        state: prevRecord.state || 1,
        parentEnumCode: prevRecord.parentEnumCode || 0,
        configId: prevRecord.configId
      };
      
      // 分别调用保存接口
      const resp1 = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', currentPayload);
      const resp2 = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', prevPayload);
      
      if (resp1.data?.code === 200 && resp2.data?.code === 200) {
        message.success('上移成功');
        
        // 清除缓存
        localStorage.removeItem('STAR_LEVEL');
        console.log('已清除STAR_LEVEL缓存');
        
        // 重新获取数据
        fetchStarLevels();
      } else {
        message.error('上移失败');
      }
    } catch (error) {
      console.error('上移星级配置异常:', error);
      message.error('上移失败');
    }
  };

  // 下移星级配置
  const moveDownStarLevel = async (record) => {
    if (!apiClient) {
      message.error('系统初始化中，请稍后重试');
      return;
    }
    
    try {
      // 获取当前排序位置
      const currentIndex = starLevels.findIndex(item => item.configId === record.configId);
      if (currentIndex >= starLevels.length - 1) {
        message.warning('已经是最后一个，无法下移');
        return;
      }
      
      // 获取下一个记录
      const nextRecord = starLevels[currentIndex + 1];
      
      // 交换showSort值
      const currentShowSort = record.showSort || 0;
      const nextShowSort = nextRecord.showSort || 0;
      
      // 更新当前记录
      const currentPayload = {
        tableName: 'STAR_LEVEL',
        colName: 'STAR_LEVEL',
        enumName: record.enumName,
        enumCode: record.enumCode || record.enumName,
        remark: JSON.stringify({ chp: record.chp }),
        showSort: nextShowSort,
        state: record.state || 1,
        parentEnumCode: record.parentEnumCode || 0,
        configId: record.configId
      };
      
      // 更新下一个记录
      const nextPayload = {
        tableName: 'STAR_LEVEL',
        colName: 'STAR_LEVEL',
        enumName: nextRecord.enumName,
        enumCode: nextRecord.enumCode || nextRecord.enumName,
        remark: JSON.stringify({ chp: nextRecord.chp }),
        showSort: currentShowSort,
        state: nextRecord.state || 1,
        parentEnumCode: nextRecord.parentEnumCode || 0,
        configId: nextRecord.configId
      };
      
      // 分别调用保存接口
      const resp1 = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', currentPayload);
      const resp2 = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', nextPayload);
      
      if (resp1.data?.code === 200 && resp2.data?.code === 200) {
        message.success('下移成功');
        
        // 清除缓存
        localStorage.removeItem('STAR_LEVEL');
        console.log('已清除STAR_LEVEL缓存');
        
        // 重新获取数据
        fetchStarLevels();
      } else {
        message.error('下移失败');
      }
    } catch (error) {
      console.error('下移星级配置异常:', error);
      message.error('下移失败');
    }
  };

  // 处理编辑
  const handleEdit = (record) => {
    console.log('编辑记录:', record); // 调试信息
    setEditingRecord(record);
    form.setFieldsValue({
      enumName: record.enumLabel,
      chp: record.chp,
      showSort: record.showSort // 保留原始的showSort值
    });
    console.log('表单设置的值:', {
      enumName: record.enumLabel,
      chp: record.chp,
      showSort: record.showSort
    }); // 调试信息
    setModalVisible(true);
  };

  // 处理新增
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    
    // 添加详细的调试信息
    console.log('=== 新增操作调试信息 ===');
    console.log('当前starLevels数组:', starLevels);
    console.log('当前starLevels长度:', starLevels.length);
    console.log('当前loading状态:', loading);
    
    // 如果数据还在加载中，等待一下
    if (loading) {
      console.log('数据正在加载中，延迟执行');
      setTimeout(() => handleAdd(), 500);
      return;
    }
    
    // 计算默认排序值：最底下那条的showSort + 10
    let maxSort = 0;
    if (starLevels.length > 0) {
      const lastItem = starLevels[starLevels.length - 1];
      console.log('最后一项数据:', lastItem);
      maxSort = (lastItem.showSort || 0) + 10;
    }
    
    console.log('计算出的maxSort值:', maxSort);
    
    form.setFieldsValue({
      showSort: maxSort
    });
    
    // 验证表单值是否正确设置
    setTimeout(() => {
      const formValues = form.getFieldsValue();
      console.log('表单当前值:', formValues);
    }, 100);
    
    setModalVisible(true);
  };

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await saveStarLevel(values);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchStarLevels();
  }, [apiClient]);

  const columns = [
    {
      title: '星级名称',
      dataIndex: 'enumLabel',
      key: 'enumLabel',
      width: 200,
    },
    {
      title: '课时费(元/课时)',
      dataIndex: 'chp',
      key: 'chp',
      width: 150,
      render: (value) => `¥${value}`,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            size="small"
            icon={<ArrowUpOutlined />}
            onClick={() => moveUpStarLevel(record)}
            disabled={starLevels.findIndex(item => item.configId === record.configId) === 0}
          >
            上移
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<ArrowDownOutlined />}
            onClick={() => moveDownStarLevel(record)}
            disabled={starLevels.findIndex(item => item.configId === record.configId) === starLevels.length - 1}
          >
            下移
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description={`确定要删除"${record.enumLabel}"的配置吗？`}
            onConfirm={() => deleteStarLevel(record)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              size="small"
              icon={<DeleteOutlined />}
              danger
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="menu-item-content">
      <Card 
        title={`星级课时费配置 (共 ${starLevels.length} 条)`}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
          >
            新增配置
          </Button>
        }
      >
        <Spin spinning={loading}>
          <Table
            dataSource={starLevels}
            columns={columns}
            rowKey="configId"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            locale={{ emptyText: '暂无数据' }}
          />
        </Spin>
      </Card>

      <Modal
        title={editingRecord ? "编辑星级配置" : "新增星级配置"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        onOk={handleSubmit}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="星级名称"
            name="enumName"
            rules={[{ required: true, message: '请输入星级名称' }]}
          >
            <Input placeholder="请输入星级名称" />
          </Form.Item>
          

          
          <Form.Item
            label="课时费(元/课时)"
            name="chp"
            rules={[
              { required: true, message: '请输入课时费' },
              { type: 'number', min: 0, message: '课时费必须大于等于0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入课时费"
              min={0}
              precision={2}
              addonAfter="元/课时"
            />
          </Form.Item>
          
          {/* 隐藏字段用于存储排序值 */}
          <Form.Item
            name="showSort"
            hidden
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StarPrice;